/**
 * When the main chat redeems a remembered guide block — as a pure function.
 *
 * The decision used to live in `ChatExchange.tsx`, in a function that lives
 * inside the component, writes refs and runs in the middle of parsing a chunk:
 * not testable. Its counterpart in the question panel is fully tested
 * (`OverlayBotChat.test.tsx`) — that asymmetry was why three findings slipped
 * past 457 green tests. So the decision now lives here, and `ChatExchange`
 * does with the result what it always did.
 *
 * It decides three things, and all three were broken:
 *
 * 1. **The entry belongs to the turn, not the conversation.** An aborted
 *    stream (user stop, timeout, exhausted retries) never reaches
 *    `endOfMessage`; the remembered jump would linger and the **next**,
 *    unrelated answer in the same conversation would trigger it. `requestId`
 *    in the entry makes that structurally impossible: anything that comes
 *    from another turn is discarded instead of redeemed.
 * 2. **Only the displayed conversation may jump.** `ChatExchange` runs
 *    several streams at once (`runsRef`). A background run finishing while
 *    the user reads another conversation would yank them to an unrelated
 *    page — and `useGuideNavigate` would hand the id of the *displayed*
 *    conversation to the question panel, i.e. the wrong one. Same guard as in
 *    `updateUI`: "Only the conversation on screen may write the displayed state."
 * 3. **The idempotency key needs the `requestId`.** Without it, the key
 *    collapses to `null:1:<address>` for every conversation without an `_id` —
 *    the same question in two fresh conversations would only jump the first time.
 */

import {
    spotlightFromHighlightBlock,
    type GuideLocale,
    type HighlightBlock,
    type NavigateBlock,
} from '../../guide/guideBlocks';
import type { PayloadResourceGroups } from './payloadResources';

/** Guide blocks reported by the running answer — one per conversation. */
export interface PendingGuideBlocks {
    highlight: HighlightBlock | null;
    navigate: NavigateBlock | null;
    /** Address of the `navigate` block, already validated. */
    href: string | null;
    /** The turn that reported it (`parsingState.requestId`). */
    requestId: number;
}

/** Pointer as the store takes it — already translated. */
export interface GuideSpotlightRequest {
    guideId: string;
    label?: string;
    hint?: string;
}

export interface GuideReleasePlan {
    /** The entry is done and must leave the map — even if nothing is executed. */
    drop: boolean;
    /** Jump request including its idempotency key, or `null`. */
    navigate: { href: string; key: string } | null;
    /** Pointer; travels with the jump if one is requested. */
    spotlight: GuideSpotlightRequest | null;
}

const NOTHING: GuideReleasePlan = { drop: false, navigate: null, spotlight: null };
const DISCARD: GuideReleasePlan = { drop: true, navigate: null, spotlight: null };

/**
 * Merges a payload's blocks into the remembered entry.
 *
 * The entry is **bound to the turn**: if the existing one comes from a
 * different `requestId`, it is replaced instead of extended. And across
 * several payloads the same rule applies as within one (`groupPayloadResources`:
 * "the first usable one wins") — otherwise the client would jump to the second
 * target while the testable pure function picks the first.
 */
export function rememberGuideBlocks(
    previous: PendingGuideBlocks | undefined,
    groups: Pick<PayloadResourceGroups, 'highlight' | 'autoNavigate' | 'autoNavigateHref'>,
    requestId: number,
): PendingGuideBlocks {
    const pending: PendingGuideBlocks =
        previous && previous.requestId === requestId
            ? previous
            : { highlight: null, navigate: null, href: null, requestId };
    if (groups.highlight) pending.highlight = groups.highlight;
    if (groups.autoNavigate && !pending.navigate) {
        pending.navigate = groups.autoNavigate;
        pending.href = groups.autoNavigateHref;
    }
    return pending;
}

/**
 * What should happen to the remembered entry on `endOfMessage`.
 *
 * Order in the plan: if the answer jumps, the pointer travels **with** the
 * jump request and is only registered after arrival — `GuideSpotlight`'s
 * search window (1.5 s) should not run out on the old page. If it does not
 * jump, the caller registers it immediately.
 */
export function planGuideRelease(input: {
    pending: PendingGuideBlocks | undefined;
    conversationId: unknown;
    /** The turn that just read `endOfMessage`. */
    requestId: number;
    messageIndex: number;
    /** Is this run's conversation the one on screen? */
    isDisplayed: boolean;
    locale: GuideLocale;
}): GuideReleasePlan {
    const { pending } = input;
    if (!pending) return NOTHING;
    // Leftovers from another turn (aborted earlier run) — discard, never redeem.
    if (pending.requestId !== input.requestId) return DISCARD;
    // Background run: discard instead of keeping, otherwise it turns into the first case.
    if (!input.isDisplayed) return DISCARD;

    const spotlight = spotlightFromHighlightBlock(pending.highlight, input.locale);
    const navigate = pending.href
        ? {
              href: pending.href,
              key: `${String(input.conversationId)}:${input.requestId}:${input.messageIndex}:${pending.href}`,
          }
        : null;
    return { drop: true, navigate, spotlight };
}
